import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AppBar,
  Breadcrumbs,
  Divider,
  IconButton,
  Stack,
  styled,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Icon, RouterButton } from '@components/base';
import { AtsLogo } from '@components/common/AtsLogo';
import { atsLogoClasses } from '@components/common/AtsLogo/utils';
import env from '@config/runtimeConfig';
import useDocumentContext from '@hooks/useDocumentContext';

import AppMenu from './AppMenu';
import DisableCacheSwitch from './DisableCacheSwitch';
import GlobalSearchField from './GlobalSearchField';
import { LocalizationSelector } from './LocalizationSelector';
import Breadcrumb from './PageTitleSegment';
import UserInfobox from './UserInfobox';

const AppBarContainer = styled(AppBar)`
  display: flex;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  color: inherit;
  min-width: 0;

  ol {
    flex-wrap: nowrap;
  }
`;

const StyledToolbar = styled(Toolbar)`
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(0, 1)};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: ${({ theme }) => theme.spacing(0, 2)};
  }
`;

const AtsButton = styled(RouterButton)`
  text-transform: none;

  .${atsLogoClasses.root} {
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const StyledDivider = styled(Divider)`
  height: 28px;
  margin-right: ${({ theme }) => theme.spacing(0.5)} !important;
  align-self: center;
`;

const AppHeader: FC = () => {
  const { t } = useTranslation();
  const { documentTitleSegments: pageTitleSegments } = useDocumentContext();
  const { breakpoints } = useTheme();
  const { pathname } = useLocation();

  const [xsOpen, setXsOpen] = useState(false);

  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));

  useEffect(() => {
    if (xsOpen) setXsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen, pathname]);

  return (
    <>
      <AppBarContainer position='fixed'>
        <StyledToolbar>
          <Stack alignItems='center' direction='row' spacing={1}>
            {isSmallScreen ? (
              <IconButton edge='start' onClick={() => setXsOpen(true)}>
                <Icon name='menu' />
              </IconButton>
            ) : (
              <AtsButton color='inherit' startIcon={<AtsLogo height={30} />} to='/'>
                <Stack>
                  <Typography component='span' variant='h6'>
                    {t('appName')}
                  </Typography>

                  <Typography
                    color='text.secondary'
                    fontWeight={500}
                    lineHeight='normal'
                    textTransform='uppercase'
                    variant='caption'
                  >
                    {t('env', { context: env })}
                  </Typography>
                </Stack>
              </AtsButton>
            )}

            {pageTitleSegments.length > 0 && (
              <Fragment>
                <StyledDivider flexItem orientation='vertical' />
                <StyledBreadcrumbs aria-label='breadcrumb' maxItems={isSmallScreen ? 2 : undefined}>
                  {pageTitleSegments.map(segment => (
                    <Breadcrumb key={segment.title} segment={segment} />
                  ))}
                </StyledBreadcrumbs>
              </Fragment>
            )}
          </Stack>

          <Stack alignItems='center' direction='row' spacing={1.5}>
            <DisableCacheSwitch />
            <GlobalSearchField />
            <LocalizationSelector />
            <UserInfobox />
          </Stack>
        </StyledToolbar>
      </AppBarContainer>

      {isSmallScreen && <AppMenu onClose={() => setXsOpen(false)} open={xsOpen} xsScreen />}
    </>
  );
};
export default AppHeader;
